import { useEffect, useState, useCallback } from "react";

interface TocItem {
  id: string;
  text: string;
  level: number;
}

export default function TableOfContents() {
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const container = document.querySelector("main article-body, main .article-body, main");
    if (!container) return;

    const headings = container.querySelectorAll("h2, h3");
    const toc: TocItem[] = [];
    headings.forEach((el, idx) => {
      if (!el.id) {
        el.id = `heading-${idx}`;
      }
      toc.push({
        id: el.id,
        text: el.textContent?.trim() || "",
        level: el.tagName === "H2" ? 2 : 3,
      });
    });
    setItems(toc);
  }, []);

  useEffect(() => {
    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
    );

    items.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [items]);

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const navHeight = 72;
    const y = el.getBoundingClientRect().top + window.scrollY - navHeight - 12;
    window.scrollTo({ top: y, behavior: "smooth" });
    setIsOpen(false);
  }, []);

  if (items.length < 3) return null;

  return (
    <>
      {/* Desktop: fixed right sidebar */}
      <div className="hidden xl:block fixed right-5 top-28 w-56 z-40">
        <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-list-check" />
            </div>
            目錄
          </h4>
          <nav className="space-y-0.5 max-h-[calc(100vh-220px)] overflow-y-auto pr-1 custom-scrollbar">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`w-full text-left text-[13px] leading-snug px-2 py-1.5 rounded-md transition-colors cursor-pointer ${
                  item.level === 3 ? "pl-5" : ""
                } ${
                  activeId === item.id
                    ? "bg-primary/5 text-primary font-medium"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                {item.text}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile: floating TOC button + drawer */}
      <div className="xl:hidden">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="fixed bottom-6 right-6 z-50 w-11 h-11 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-600 hover:text-primary transition-colors"
          aria-label="文章目錄"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-list-check" />
          </div>
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
              onClick={() => setIsOpen(false)}
            />
            <div className="fixed bottom-20 right-4 z-50 w-72 bg-white rounded-xl border border-slate-100 shadow-lg p-4 max-h-[60vh] flex flex-col">
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-list-check" />
                </div>
                目錄
              </h4>
              <nav className="space-y-0.5 overflow-y-auto custom-scrollbar pr-1">
                {items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    className={`w-full text-left text-[13px] leading-snug px-2 py-1.5 rounded-md transition-colors cursor-pointer ${
                      item.level === 3 ? "pl-5" : ""
                    } ${
                      activeId === item.id
                        ? "bg-primary/5 text-primary font-medium"
                        : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {item.text}
                  </button>
                ))}
              </nav>
            </div>
          </>
        )}
      </div>
    </>
  );
}